import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { PageHero } from "@/components/page-hero";
import { formatDateLong } from "@/lib/format-date";
import { getBlogPost, blogPosts, type BlogPost } from "@/content/blog";
import { CTASection } from "@/components/cta-section";
import { SeoFAQ } from "@/components/seo-faq";
import { AccreditationsStrip } from "@/components/accreditations-strip";
import { Breadcrumbs } from "@/components/breadcrumbs";
import { ReviewStamp } from "@/components/review-stamp";
import { RelatedContent } from "@/components/related-content";
import { getRelatedContent } from "@/lib/related";
import { ALL_AUTHORS } from "@/content/authors";
import { buildArticleHead } from "@/lib/article-head";
import { generatePageTitle, ensureFAQs } from "@/lib/seo";


const pkFor = (post: BlogPost) => post.primaryKeyword ?? post.slug.replace(/-/g, " ");

export const Route = createFileRoute("/hr-insights/$slug")({
  loader: ({ params }) => {
    const post = getBlogPost(params.slug);
    if (!post) throw notFound();
    return { post };
  },
  head: ({ loaderData }) => {
    if (!loaderData) return {};
    void ALL_AUTHORS;
    return buildArticleHead(loaderData.post);
  },

  component: BlogPostPage,
});

function BlogPostPage() {
  const { post } = Route.useLoaderData() as { post: BlogPost };
  const related = blogPosts.filter((p) => p.slug !== post.slug).slice(0, 2);
  const pk = pkFor(post);

  return (
    <>
      <PageHero
        breadcrumbSchema={false}
        breadcrumbs={[
          { label: "Home", path: "/" },
          { label: "Journal", path: "/hr-insights" },
          { label: post.title.slice(0, 50), path: `/hr-insights/${post.slug}` },
        ]}
        eyebrow={post.category}
        title={post.title}
        subtitle={`A UK employer's guide to ${post.title.toLowerCase()}.`}
        intro={`${post.readingTime} min read · By ${post.author}, ${post.authorCredentials}.`}
        lastReviewed={post.lastReviewed}
        author={{
          name: post.author,
          credentials: post.authorCredentials,
          slug: ALL_AUTHORS.find((a) => a.name === post.author)?.slug,
        }}
      />


      <section className="px-6 py-16 lg:px-8">
        <div className="mx-auto grid max-w-6xl grid-cols-1 gap-12 lg:grid-cols-12">
          <article className="lg:col-span-8">
            <div className="rounded-2xl border border-accent/20 bg-accent/5 p-6">
              <p className="text-xs font-semibold uppercase tracking-widest text-accent">Key takeaways</p>
              <ul className="mt-4 space-y-2 text-sm leading-relaxed text-foreground">
                {post.keyTakeaways.map((t) => (
                  <li key={t} className="flex gap-3">
                    <span className="mt-2 size-1.5 shrink-0 rounded-full bg-accent" />
                    {t}
                  </li>
                ))}
              </ul>
            </div>

            <div className="article-markdown mt-10">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{post.bodyMarkdown}</ReactMarkdown>
            </div>

            <p className="mt-10 rounded-xl bg-card p-4 text-xs leading-relaxed text-muted-foreground ring-1 ring-border">
              This content is for general information only and does not constitute legal advice. For advice on your
              specific situation, please contact one of our HR experts.
            </p>
          </article>

          <aside className="lg:col-span-4">
            <div className="sticky top-24 space-y-6">
              {post.hubService && (
                <div className="rounded-2xl border border-accent/30 bg-accent/5 p-6">
                  <p className="text-xs font-semibold uppercase tracking-widest text-accent">Part of</p>
                  <p className="mt-3 font-serif text-lg text-brand">{post.hubService.name}</p>
                  <p className="mt-2 text-sm text-muted-foreground">
                    This guide covers the procedure. For end-to-end employer support running the programme, visit the hub service.
                  </p>
                  <Link
                    to="/services/$slug"
                    params={{ slug: post.hubService.slug }}
                    className="mt-4 inline-flex w-full items-center justify-center rounded-md bg-brand px-4 py-2.5 text-sm font-medium text-brand-foreground hover:brightness-110"
                  >
                    See {post.hubService.name} →
                  </Link>
                </div>
              )}

              <div className="rounded-2xl bg-brand p-6 text-brand-foreground">
                <p className="font-serif text-xl">Need help with this?</p>
                <p className="mt-2 text-sm text-brand-foreground/70">
                  Speak to a senior advisor about your specific situation, usually free for SMEs.
                </p>
                <Link
                  to="/contact-hr-experts"
                  className="mt-4 inline-flex w-full items-center justify-center rounded-md bg-accent px-4 py-2.5 text-sm font-medium text-accent-foreground hover:brightness-110"
                >
                  Get a free consultation
                </Link>
              </div>

              <div className="rounded-2xl border border-border bg-card p-6">
                <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Related</p>
                <ul className="mt-4 space-y-4">
                  {related.map((r) => (
                    <li key={r.slug}>
                      <Link
                        to="/hr-insights/$slug"
                        params={{ slug: r.slug }}
                        className="font-serif text-base text-brand hover:text-accent"
                      >
                        {r.title}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </aside>
        </div>
      </section>

      <AccreditationsStrip />
      <SeoFAQ primaryKeyword={pk} type="blog" faqs={post.faqs} />

      <RelatedContent
        heading={`More on ${pk}`}
        items={getRelatedContent({
          type: "blog",
          slug: post.slug,
          primaryKeyword: pk,
          topics: [post.category],
          diversify: true,
        })}
      />

      <CTASection />
    </>
  );
}
